import React, { useCallback, useState } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useCreateLowInventoryNotification } from '~/hooks/apis/price-parity/useCreateLowInventoryNotification';
import { useGetUnsolvedLowInventoryNotifications } from '~/hooks/apis/price-parity/useGetUnsolvedLowInventoryNotifications';
import { useSendNotificationEmail } from '~/hooks/apis/price-parity/useSendNotificationEmail';
import { useVendorOfferSelection } from '~/hooks/apis/price-parity/useVendorOfferSelection';

import type { LowInventoryNotificationBody } from '~/types/services/parityNotifications';

import VendorOfferSelector from '../shared/VendorOfferSelector';
import { genSalesforceId } from '../shared/utils';

import LowInventoryNotificationsGrid from './LowInventoryNotificationsGrid';
import MonthsFieldArray from './MonthsFieldArray';
import SharpDropFields from './SharpDropFields';
import type { FormValues } from './types';

const LOW_INVENTORY_TYPES = [
  { value: 'low_inventory', label: 'Low Inventory' },
  { value: 'sharp_drop', label: 'Sharp Drop' },
];

function currentYearMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export default function LowInventoryNotifications() {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      type: 'low_inventory',
      caseId: '',
      vendorId: '',
      offerId: '',
      propertyId: '',
      months: [{ value: currentYearMonth() }],
      threshold: 50,
      leadRatePlanBk: '',
      leadRatePlanName: '',
      inventoryDropFlag: true,
      inventoryDropPct: undefined,
      prevTotalInventoryCount: undefined,
      totalInventoryCount: undefined,
    },
  });

  const [leadRatePlanId, setLeadRatePlanId] = useState('');

  const notificationType = useWatch({ control, name: 'type' });
  const isSharpDrop = notificationType === 'sharp_drop';
  const vendorIdValue = useWatch({ control, name: 'vendorId' });

  const vendorOfferSelection = useVendorOfferSelection(vendorIdValue);

  const {
    data: notifications = [],
    isLoading: notificationsLoading,
    error: notificationsError,
  } = useGetUnsolvedLowInventoryNotifications();

  const { sendEmailMutation, sendingEmailId } = useSendNotificationEmail();

  const handleReset = useCallback(() => {
    reset();
    vendorOfferSelection.reset();
    setLeadRatePlanId('');
  }, [reset, vendorOfferSelection]);

  const createMutation = useCreateLowInventoryNotification(handleReset);

  const onSubmit = (data: FormValues) => {
    const body: LowInventoryNotificationBody = {
      type: data.type,
      caseId: data.caseId,
      vendorId: data.vendorId,
      offerId: data.offerId,
      propertyId: data.propertyId,
      months: data.months.map((m) => m.value).filter(Boolean),
    };
    if (data.threshold != null) body.threshold = Number(data.threshold);
    if (data.leadRatePlanBk) body.lead_rate_plan_bk = data.leadRatePlanBk;
    if (data.leadRatePlanName) body.lead_rate_plan_name = data.leadRatePlanName;
    if (isSharpDrop) {
      body.inventory_drop_flag = data.inventoryDropFlag ?? true;
      body.inventory_drop_pct = Number(data.inventoryDropPct);
      body.prev_total_inventory_count = Number(data.prevTotalInventoryCount);
      body.total_inventory_count = Number(data.totalInventoryCount);
    }
    createMutation.mutate(body);
  };

  const ratePlans = vendorOfferSelection.selectionData?.ratePlans ?? [];

  const handleSendEmail = useCallback((id: string) => sendEmailMutation.mutate(id), [sendEmailMutation]);

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Create Low Inventory Notification (Staging Only)
      </Typography>

      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3}>
        {/* ── Type ── */}
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <TextField {...field} select label="Type" required disabled={isSubmitting} sx={{ maxWidth: 240 }}>
              {LOW_INVENTORY_TYPES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {/* ── Vendor ID ── */}
        <Controller
          name="vendorId"
          control={control}
          rules={{ required: 'Required' }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Vendor ID"
              required
              disabled={isSubmitting}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ minWidth: 300, maxWidth: 340 }}
              InputProps={{
                endAdornment: vendorOfferSelection.lookupLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />

        {/* ── Offer + Property selectors ── */}
        <VendorOfferSelector
          offers={vendorOfferSelection.offers}
          offersFetching={vendorOfferSelection.offersFetching}
          vendorEnabled={vendorOfferSelection.vendorEnabled}
          selectedOfferId={vendorOfferSelection.selectedOfferId}
          setSelectedOfferId={vendorOfferSelection.setSelectedOfferId}
          selectionProperties={vendorOfferSelection.selectionData?.properties}
          selectionFetching={vendorOfferSelection.selectionFetching}
          setValue={setValue}
          showProperty
          disabled={isSubmitting}
        />

        {/* ── Manual ID fields ── */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Controller
            name="caseId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Case ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 260 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Generate a fake Salesforce Case ID">
                        <Button
                          size="small"
                          variant="text"
                          sx={{ minWidth: 0, fontSize: 11 }}
                          onClick={() => setValue('caseId', genSalesforceId('500'))}
                          tabIndex={-1}
                        >
                          Gen
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Controller
            name="offerId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Offer ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 260 }}
              />
            )}
          />
          <Controller
            name="propertyId"
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Property ID"
                required
                disabled={isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ minWidth: 260 }}
              />
            )}
          />
          <Controller
            name="threshold"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
                type="number"
                label="Threshold %"
                disabled={isSubmitting}
                sx={{ minWidth: 140 }}
                helperText="Default: 50"
              />
            )}
          />
        </Stack>

        {/* ── Lead rate plan ── */}
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {ratePlans.length > 0 && (
            <TextField
              select
              label="Lead Rate Plan"
              helperText="Sets Lead Rate Plan BK and Name"
              value={leadRatePlanId}
              onChange={(e) => {
                const rp = ratePlans.find((r) => r.id === e.target.value);
                setLeadRatePlanId(e.target.value);
                setValue('leadRatePlanBk', rp?.id ?? '');
                setValue('leadRatePlanName', rp?.name ?? '');
              }}
              sx={{ minWidth: 300 }}
              disabled={isSubmitting}
            >
              <MenuItem value="">—</MenuItem>
              {ratePlans.map((rp) => (
                <MenuItem key={rp.id} value={rp.id}>
                  {rp.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Controller
            name="leadRatePlanBk"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Lead Rate Plan BK" disabled={isSubmitting} sx={{ minWidth: 240 }} />
            )}
          />
          <Controller
            name="leadRatePlanName"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Lead Rate Plan Name" disabled={isSubmitting} sx={{ minWidth: 240 }} />
            )}
          />
        </Stack>

        {/* ── Sharp drop fields ── */}
        {isSharpDrop && <SharpDropFields control={control} isSubmitting={isSubmitting} />}

        {/* ── Months ── */}
        <MonthsFieldArray control={control} isSubmitting={isSubmitting} />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="text" disabled={isSubmitting} onClick={handleReset}>
            Clear
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            Create Notification
          </Button>
        </Stack>
      </Stack>

      {/* ── Recent notifications table ── */}
      <LowInventoryNotificationsGrid
        notifications={notifications}
        isLoading={notificationsLoading}
        error={notificationsError}
        onSendEmail={handleSendEmail}
        sendingId={sendingEmailId}
      />
    </Box>
  );
}
